import PropTypes from "prop-types";

import {
  SERIES,
  formatBrl,
  formatMonthLabel,
} from "./monthlyProjectionModel";
import {
  buildMonthSignals,
  computeMonthComparisons,
  monthTiming,
} from "./monthlyProjectionInsightRules";

const formatPercentage = (percentage, label) => {
  if (!label) return null;
  if (percentage.status === "value") {
    const text = percentage.value.toLocaleString("pt-BR", {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
    return `${label}: ${text}%`;
  }
  if (percentage.status === "suppressed") return `${label}: suprimido por valor negativo`;
  return `${label}: Não aplicável`;
};

const cellText = (cell) => (cell?.status === "value" ? formatBrl(cell.value) : "—");

const cellNote = (cell, availability) => {
  if (cell?.status === "value") return null;
  if (availability === "unavailable") return "Série não disponível";
  return "Sem valor informado para o mês";
};

const MonthlyProjectionInsights = ({ monthKey, cells, availability, now, measurementReference }) => {
  const comparisons = computeMonthComparisons(cells);
  const signals = buildMonthSignals({ monthKey, cells, now });
  const timing = monthTiming(monthKey, now);
  const label = formatMonthLabel(monthKey);

  return (
    <section
      className="monthly-projection-reading"
      role="region"
      aria-label={`Leitura de ${label}`}
      aria-live="polite"
    >
      <header className="monthly-projection-reading-header">
        <h4>Leitura de {label}</h4>
        <p>
          Valores do mês, não acumulados.
          {timing === "past" && measurementReference
            ? ` Medição conforme a última análise publicada (${measurementReference}).`
            : ""}
        </p>
      </header>

      <div className="monthly-projection-values" role="list" aria-label={`Cinco valores de ${label}`}>
        {SERIES.map((series) => {
          const cell = cells[series.key];
          const note = cellNote(cell, availability?.[series.key]);
          return (
            <div
              className="monthly-projection-value"
              role="listitem"
              key={series.key}
              style={{ borderTopColor: series.color }}
            >
              <span className="monthly-projection-value-label">{series.label}</span>
              <strong className="monthly-projection-value-amount">{cellText(cell)}</strong>
              <small>{note ?? series.meaning}</small>
            </div>
          );
        })}
      </div>

      <div className="monthly-projection-differences">
        {comparisons.map((comparison) => (
          <article
            className={`monthly-projection-difference tone-${comparison.tone}`}
            key={comparison.key}
          >
            <span className="monthly-projection-difference-label">{comparison.label}</span>
            <strong className="monthly-projection-difference-amount">
              {comparison.difference.status === "value" ? formatBrl(comparison.difference.value) : "—"}
            </strong>
            <small className="monthly-projection-difference-formula">{comparison.formula}</small>
            <p>{comparison.text}</p>
            {formatPercentage(comparison.percentage, comparison.percentageLabel) && (
              <small>{formatPercentage(comparison.percentage, comparison.percentageLabel)}</small>
            )}
            <small className="monthly-projection-difference-caveat">{comparison.caveat}</small>
          </article>
        ))}
      </div>

      {signals.length > 0 && (
        <ul className="monthly-projection-signals" aria-label={`Observações de ${label}`}>
          {signals.map((item) => (
            <li key={item.id} className={`monthly-projection-signal tone-${item.tone}`}>
              {item.text}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

MonthlyProjectionInsights.propTypes = {
  monthKey: PropTypes.string.isRequired,
  cells: PropTypes.object.isRequired,
  availability: PropTypes.object,
  now: PropTypes.instanceOf(Date),
  measurementReference: PropTypes.string,
};

MonthlyProjectionInsights.defaultProps = {
  availability: {},
  now: undefined,
  measurementReference: null,
};

const ACTIONS = [
  {
    kind: "activities",
    label: "Ver atividades",
    scope: "Atividades vinculadas ao item, na posição publicada pela análise. O status de cada atividade não é o histórico do mês selecionado.",
  },
  {
    kind: "paid",
    label: "Ver pagamentos",
    scope: "Títulos pagos do mesmo item, pelo mês de movimento do mês em foco.",
  },
  {
    kind: "toPay",
    label: "Ver a pagar",
    scope: "Saldo aberto do mesmo item, pelo mês de vencimento do mês em foco.",
  },
];

const formatReference = (reference) => {
  if (!reference?.syncedAt) return null;
  const date = new Date(reference.syncedAt);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString("pt-BR");
};

export const MonthlyProjectionInvestigation = ({ monthKey, wbs, typeLabel, investigations, onInvestigate }) => {
  const label = formatMonthLabel(monthKey);
  const activityReference = formatReference(investigations?.activities?.reference);
  return (
    <section className="monthly-projection-investigation" role="region" aria-label={`Investigar ${label}`}>
      <h4>Investigar {label}</h4>
      <p>
        Item {wbs}{typeLabel ? ` · nível ${typeLabel}` : ""} · mês {label}. As listas mantêm o item, o mês e a natureza; paginação e busca não alteram o gráfico.
      </p>
      <ul className="monthly-projection-actions">
        {ACTIONS.map((action) => {
          const state = investigations?.[action.kind] ?? { available: false };
          return (
            <li key={action.kind}>
              <button
                type="button"
                className="button button-accent small"
                disabled={!state.available || !monthKey}
                onClick={() => onInvestigate?.(action.kind, { monthKey, wbs })}
              >
                {action.label}
              </button>
              <small>{state.available ? action.scope : (state.reason ?? "Rota indisponível para este item e mês.")}</small>
            </li>
          );
        })}
      </ul>
      {activityReference && (
        <p className="monthly-projection-reference">
          Atividades na posição publicada em {activityReference}
          {investigations?.activities?.reference?.paginated ? "; a lista é paginada e não representa um ranking global." : "."}
        </p>
      )}
    </section>
  );
};

MonthlyProjectionInvestigation.propTypes = {
  monthKey: PropTypes.string,
  wbs: PropTypes.string,
  typeLabel: PropTypes.string,
  investigations: PropTypes.object,
  onInvestigate: PropTypes.func,
};

MonthlyProjectionInvestigation.defaultProps = {
  monthKey: null,
  wbs: "",
  typeLabel: "",
  investigations: {},
  onInvestigate: undefined,
};

export default MonthlyProjectionInsights;
